/**
 * Dynamic Activity Page
 *
 * Renders a full activity booking page for admin-created activities
 * stored in Firestore. Resolves the real slug from the URL (handles
 * Firebase Hosting _placeholder rewrite), fetches activity data from
 * the API, and renders the same page structure as static adventures.
 */

'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useActivity, Activity } from '@/lib/hooks/useActivities';
import ActivityBookingClient from './ActivityBookingClient';
import styles from './page.module.css';

interface Props {
  initialSlug: string;
}

/** Build stats cards from activity data */
function buildStats(
  activity: Activity,
  labels: {
    duration: string;
    durationDesc: string;
    perPerson: string;
    difficulty: string;
    difficultyLevel: (d: string) => string;
    skillRequired: string;
    location: string;
    category: string;
    activityType: string;
    priceIn: (currency: string) => string;
  }
) {
  const stats: Array<{ number: string; label: string; description: string }> = [];

  if (activity.durationHours) {
    stats.push({
      number: `${activity.durationHours}h`,
      label: labels.duration,
      description: labels.durationDesc,
    });
  }

  if (activity.basePrice?.amount) {
    const currency = activity.basePrice.currency === 'CLP' ? 'CLP' : 'USD';
    const symbol = '$';
    stats.push({
      number: `${symbol}${activity.basePrice.amount.toLocaleString()}`,
      label: labels.perPerson,
      description: labels.priceIn(currency),
    });
  }

  if (activity.difficulty) {
    stats.push({
      number: labels.difficultyLevel(activity.difficulty),
      label: labels.difficulty,
      description: labels.skillRequired,
    });
  }

  if (activity.location?.name) {
    stats.push({
      number: activity.location.name.split(',')[0].trim(),
      label: labels.location,
      description: activity.location.name,
    });
  }

  // Ensure at least 2 stats
  if (stats.length < 2 && activity.category) {
    stats.push({
      number: activity.category.charAt(0).toUpperCase() + activity.category.slice(1),
      label: labels.category,
      description: labels.activityType,
    });
  }

  return stats;
}

/** Convert Firestore Activity to Adventure shape for ActivityBookingClient */
function toAdventureShim(activity: Activity) {
  const name = activity.name;
  return {
    id: activity.id,
    title: name,
    shortTitle: name,
    description: activity.description || activity.shortDescription || '',
    heroImage: activity.heroImage || '/images/placeholder-activity.webp',
    link: `/booking/activities/${activity.slug || activity.id}`,
    subtitle: activity.subtitle || activity.shortDescription || '',
    location: activity.location?.name || '',
    bookingMode: 'direct' as const,
  };
}

export default function DynamicActivityPage({ initialSlug }: Props) {
  const t = useTranslations('bookingActivity');

  // Difficulty display labels (translated)
  const DIFFICULTY_LABELS: Record<string, string> = {
    easy: t('dynamic.difficulty.easy'),
    moderate: t('dynamic.difficulty.moderate'),
    challenging: t('dynamic.difficulty.challenging'),
    expert: t('dynamic.difficulty.expert'),
  };

  // Resolve real slug from URL (handles _placeholder rewrite)
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    // URL: /booking/activities/{slug} → last segment is the real slug
    const urlSlug = pathParts[pathParts.length - 1];
    if (urlSlug && urlSlug !== '_placeholder') {
      setResolvedSlug(urlSlug);
    } else if (initialSlug && initialSlug !== '_placeholder') {
      setResolvedSlug(initialSlug);
    }
  }, [initialSlug]);

  // Fetch activity data from Firestore API
  const { activity, loading, error } = useActivity(resolvedSlug);

  // Loading state — premium skeleton matching static pages
  if (loading || !resolvedSlug) {
    return (
      <div className={styles.bookingPage}>
        <section className={styles.hero} data-hero-background>
          <div className={styles.heroBackground}>
            <div className={`${styles.skeleton} ${styles.skeletonHeroBg}`} />
            <div className={styles.heroOverlay} />
          </div>
          <div className={styles.heroContent}>
            <div className={styles.skeletonBreadcrumb}>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>/</span>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>/</span>
              <div className={`${styles.skeleton} ${styles.skeletonBreadcrumbItem}`} />
            </div>
            <div className={`${styles.skeleton} ${styles.skeletonHeroTitle}`} />
            <div className={`${styles.skeleton} ${styles.skeletonHeroSubtitle}`} />
          </div>
        </section>
        <section className={styles.statsSection}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.statCard}>
                  <div className={`${styles.skeleton} ${styles.skeletonStatNumber}`} style={{ margin: '0 auto 0.5rem' }} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatLabel}`} style={{ margin: '0 auto 0.25rem' }} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatDesc}`} style={{ margin: '0 auto' }} />
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className={styles.bookingSectionSkeleton}>
          <div className={styles.container}>
            <div className={`${styles.skeleton} ${styles.skeletonBookingWidget}`} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !activity) {
    return (
      <div className={styles.bookingPage}>
        <section className={styles.ctaSection} style={{ minHeight: '60vh', display: 'flex', alignItems: 'center' }}>
          <div className={styles.container}>
            <div className={styles.ctaContent}>
              <h1 className={styles.ctaTitle}>{t('dynamic.notFound.title')}</h1>
              <p className={styles.ctaDescription}>
                {error || t('dynamic.notFound.description', { slug: resolvedSlug })}
              </p>
              <div className={styles.ctaButtons}>
                <a href="/booking/activities" className={styles.ctaButtonPrimary}>
                  {t('dynamic.notFound.viewAll')}
                </a>
                <a href="/contact" className={styles.ctaButtonSecondary}>
                  {t('dynamic.notFound.contact')}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const statsLabels = {
    duration: t('dynamic.stats.duration'),
    durationDesc: t('dynamic.stats.durationDesc'),
    perPerson: t('dynamic.stats.perPerson'),
    difficulty: t('dynamic.stats.difficulty'),
    difficultyLevel: (d: string) => DIFFICULTY_LABELS[d] || d,
    skillRequired: t('dynamic.stats.skillRequired'),
    location: t('dynamic.stats.location'),
    category: t('dynamic.stats.category'),
    activityType: t('dynamic.stats.activityType'),
    priceIn: (currency: string) => t('dynamic.stats.priceIn', { currency }),
  };

  const stats = buildStats(activity, statsLabels);
  const adventure = toAdventureShim(activity);
  const heroSrc = activity.heroImage || '';
  const isExternalImage = heroSrc.startsWith('http');
  const focalPoint = activity.focalPoint || { x: 50, y: 50 };
  const focalCss = `${focalPoint.x}% ${focalPoint.y}%`;
  const activityName = activity.name;
  const activityDescription = activity.description || '';
  const subtitle = activity.subtitle
    || activity.shortDescription
    || activityDescription.substring(0, 120)
    || '';
  const locationName = activity.location?.name || '';
  const included = activity.included || [];
  const whatToBring = activity.whatToBring || activity.requirements || [];
  const galleryImages = activity.images || [];

  return (
    <div className={styles.bookingPage}>
      {/* Hero Section */}
      <section className={styles.hero} data-hero-background>
        <div className={styles.heroBackground}>
          {heroSrc ? (
            isExternalImage ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={heroSrc}
                alt={`${activityName} - ${t('dynamic.hero.altSuffix')}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalCss }}
              />
            ) : (
              <Image
                src={heroSrc}
                alt={`${activityName} - ${t('dynamic.hero.altSuffix')}`}
                fill
                priority
                sizes="100vw"
                className={styles.heroImage}
                style={{ objectPosition: focalCss }}
              />
            )
          ) : (
            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }} />
          )}
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <div className={styles.breadcrumb}>
            <a href="/booking" className={styles.breadcrumbLink}>{t('breadcrumb.booking')}</a>
            <span className={styles.breadcrumbSeparator}>/</span>
            <a href="/booking/activities" className={styles.breadcrumbLink}>{t('breadcrumb.activities')}</a>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>{activityName}</span>
          </div>
          <h1 className={styles.heroTitle}>{activityName}</h1>
          {subtitle && <p className={styles.heroSubtitle}>{subtitle}</p>}
          {locationName && (
            <div className={styles.heroLocation}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>{locationName}</span>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      {stats.length > 0 && (
        <section className={styles.statsSection}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {stats.map((stat, i) => (
                <div key={i} className={styles.statCard}>
                  <div className={styles.statNumber}>{stat.number}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                  <div className={styles.statDescription}>{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking Section — reuses existing ActivityBookingClient */}
      <ActivityBookingClient activity={adventure} />

      {/* Details Section */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            <div className={styles.detailsContent}>
              <h2 className={styles.detailsTitle}>{t('about')}</h2>
              <p className={styles.detailsDescription}>
                {activityDescription || t('dynamic.details.fallbackDescription')}
              </p>

              {/* What's Included */}
              {included.length > 0 && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('whatsIncluded')}</h3>
                  <ul className={styles.detailsList}>
                    {included.map((item, i) => (
                      <li key={i} className={styles.detailsItem}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What to Bring */}
              {whatToBring.length > 0 && (
                <div className={styles.detailsBlock}>
                  <h3 className={styles.blockTitle}>{t('whatToBring')}</h3>
                  <ul className={styles.detailsList}>
                    {whatToBring.map((item, i) => (
                      <li key={i} className={styles.detailsItem}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Gallery */}
            <div className={styles.galleryContainer}>
              <div className={styles.gallery}>
                {galleryImages.length > 0 ? (
                  galleryImages.slice(0, 6).map((img, i) => (
                    <div key={i} className={styles.galleryItem}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={img}
                        alt={`${activityName} - ${t('dynamic.gallery.imageAlt', { index: i + 1 })}`}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                      />
                    </div>
                  ))
                ) : heroSrc ? (
                  <div className={styles.galleryItem}>
                    {isExternalImage ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={heroSrc}
                        alt={activityName}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                      />
                    ) : (
                      <Image
                        src={heroSrc}
                        alt={activityName}
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        className={styles.galleryImage}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('dynamic.cta.title')}</h2>
            <p className={styles.ctaDescription}>
              {t('dynamic.cta.description', { name: activityName })}
            </p>
            <div className={styles.ctaButtons}>
              <a href="/contact" className={styles.ctaButtonPrimary}>{t('dynamic.cta.contact')}</a>
              <a href="/booking/activities" className={styles.ctaButtonSecondary}>{t('dynamic.cta.viewAll')}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
